/* eslint-disable no-console */
import { useDisclosure } from "@ch/helpers/hooks/useDisclosure";
import useKemUserSession from "@ch/helpers/hooks/useKemUserSession";
import useLogout from "@ch/helpers/hooks/useLogout";
import { getDateOnly } from "@ch/helpers/utils";
import useFirebaseAnalytics from "@ch/helpers/utils/firebase/hooks/useFirebaseAnalytics";
import { FirebaseEventEnum } from "@ch/helpers/utils/firebase/types";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import { updateAppKemState } from "@ch/redux-store/kem/actions";
import type { IKemState } from "@ch/redux-store/kem/reducer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import KemCoachSocket, {
  type KemCoachCompletionData,
  type KemCoachMemberPayload,
  type KemCoachSocketState,
} from "../utils/kemCoachSocket";
import type {
  TGetChatObject,
  TKemMessagePayload,
  TQuickOption,
  TSendMessageProps,
} from "../utils/types";
import useGetHistory from "./useGetHistory";
import useKemDefaultPayload from "./useKemDefaultPayload";

const historyItemsPerBatch = 10;
const KEM_IC_COMPLETION_PENDING_STORAGE_KEY = "kem_ic_completion_pending";

interface UseKemCoachChatProps {
  currentWeek?: string;
}

const uniqBy = <T>(arr: T[], key: (item: T) => unknown): T[] => {
  const seen = new Set<unknown>();
  return arr.filter((item) => {
    const k = key(item);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const toErrorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

const useKemCoachChat = ({ currentWeek }: UseKemCoachChatProps = {}) => {
  const user = useAppSelector((state) => state.user?.currentUser);
  const kemUserSession = useAppSelector((state) => state.kem.kemUserSession);
  const activeSessionKey = useAppSelector(
    (state) => state.kem.activeSessionKey,
  );
  const activeICSessionKey = useAppSelector(
    (state) => state.kem.activeICSessionKey,
  );

  const [socketState, setSocketState] =
    useState<KemCoachSocketState>("DISCONNECTED");
  const [currentChat, setCurrentChat] = useState<TKemMessagePayload[]>([]);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [loadingBotChat, setLoadingBotChat] = useState<
    "sending" | "streaming"
  >();
  const [currentQuickOptions, setCurrentQuickOptions] = useState<
    TQuickOption[]
  >([]);

  const socketRef = useRef<KemCoachSocket | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentBotMessageIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const shouldSendMemberPayloadRef = useRef(false);
  const hasLoadedHistoryRef = useRef(false);

  const lastHistoryLoadKeyRef = useRef<string | null>(null);

  const userId = user?.id;

  const userFullName = user?.name
    ? user.name.trim()
    : `${user?.firstName?.trim()} ${user?.lastName?.trim()}`;

  const isKemIC = currentWeek === "-1";

  const sessionKey = isKemIC
    ? (activeICSessionKey ??
      kemUserSession?.ic_session_key ??
      kemUserSession?.session_key)
    : (activeSessionKey ?? kemUserSession?.session_key);
  const sessionCompleted = kemUserSession?.ic_state === "completed";
  const prevSessionCompletedRef = useRef(sessionCompleted);

  const { LogEvent } = useFirebaseAnalytics();
  const { defaultChatPayload } = useKemDefaultPayload();

  const { getNextChatHistory, getIsLastPage, getChatHistory } = useGetHistory({
    userId,
    pageLimit: historyItemsPerBatch,
    sessionKey,
    enabled: !!sessionKey,
    findBySessionKey: true,
  });

  const { logoutUser } = useLogout();

  const {
    isOpen: isSessionCompletedModalOpen,
    onOpen: onOpenSessionCompletedModal,
    onClose: onCloseSessionCompletedModal,
  } = useDisclosure();

  const { initiateKemUserSession } = useKemUserSession();
  const dispatch = useDispatch();

  const icCompletionProgress = useAppSelector(
    (state) => state.kem.icCompletionProgress,
  );
  const coachCompletionProgress = useAppSelector(
    (state) => state.kem.coachCompletionProgress,
  );
  const icCompletionProgressRef = useRef(icCompletionProgress ?? 0);
  const coachCompletionProgressRef = useRef(coachCompletionProgress ?? 0);

  useEffect(() => {
    icCompletionProgressRef.current = icCompletionProgress ?? 0;
  }, [icCompletionProgress]);

  useEffect(() => {
    coachCompletionProgressRef.current = coachCompletionProgress ?? 0;
  }, [coachCompletionProgress]);

  /** Monotonic progress updates from completion meta only. */
  const applyCompletionProgress = useCallback(
    (progress: number) => {
      if (currentWeek === "-1") {
        const next = Math.max(icCompletionProgressRef.current, progress);
        icCompletionProgressRef.current = next;
        dispatch(updateAppKemState({ icCompletionProgress: next }));
        return;
      }

      const next = Math.max(coachCompletionProgressRef.current, progress);
      coachCompletionProgressRef.current = next;
      dispatch(updateAppKemState({ coachCompletionProgress: next }));
    },
    [currentWeek, dispatch],
  );

  const isReady =
    socketState === "READY" ||
    socketState === "COMPLETE" ||
    socketState === "STREAMING" ||
    socketState === "RECONNECTING" ||
    socketState === "ERROR";

  const kemIsTyping = loadingBotChat === "sending" ? "Kem is typing..." : "";

  const isInitialHistoryLoading = useMemo(
    () => isLoadingHistory && currentChat.length === 0,
    [currentChat.length, isLoadingHistory],
  );

  /** Socket reachable (not ERROR / DISCONNECTED); distinct from HTTP Kem health on `/kem/chat`. */
  const kemSocketReachable = useMemo(
    () => socketState !== "ERROR" && socketState !== "DISCONNECTED",
    [socketState],
  );

  const isConnecting = useMemo(
    () => socketState === "CONNECTING",
    [socketState],
  );

  const defaultChatProps = useMemo(() => {
    const latestBMI = defaultChatPayload?.latest_bmi?.split(" ")[0];

    return {
      ...defaultChatPayload,
      bmi_measurement_date: getDateOnly(
        defaultChatPayload?.bmi_measurement_date,
      ),
      bp_measurement_date: getDateOnly(defaultChatPayload?.bp_measurement_date),
      date_of_birth: getDateOnly(defaultChatPayload?.date_of_birth),
      latest_bmi: latestBMI ? Number(latestBMI) : undefined,
      exercise_duration_last_30_days:
        defaultChatPayload?.exercise_duration_last_30_days?.toString(),
      location: defaultChatPayload?.location?.trim(),
      user_full_name: defaultChatPayload?.user_full_name ?? userFullName,
      user_id: defaultChatPayload?.user_id ?? userId,
      blood_sugar_measurement_date: getDateOnly(
        defaultChatPayload?.blood_sugar_measurement_date,
      ),
      cholesterol_measurement_date: getDateOnly(
        defaultChatPayload?.cholesterol_measurement_date,
      ),
      height_measurement_date: getDateOnly(
        defaultChatPayload?.height_measurement_date,
      ),
      weight_measurement_date: getDateOnly(
        defaultChatPayload?.weight_measurement_date,
      ),
      weight_goal_measurement_date: getDateOnly(
        defaultChatPayload?.weight_goal_measurement_date,
      ),
      waist_circumference_measurement_date: getDateOnly(
        defaultChatPayload?.waist_circumference_measurement_date,
      ),
      hbaic_measurement_date: getDateOnly(
        defaultChatPayload?.hbaic_measurement_date,
      ),
      eye_examination_measurement_date: getDateOnly(
        defaultChatPayload?.eye_examination_measurement_date,
      ),
      foot_examination_measurement_date: getDateOnly(
        defaultChatPayload?.foot_examination_measurement_date,
      ),
      health_condition_measurement_date: getDateOnly(
        defaultChatPayload?.health_condition_measurement_date,
      ),
      procedure_surgeries_measurement_date: getDateOnly(
        defaultChatPayload?.procedure_surgeries_measurement_date,
      ),
      prescription_measurement_date: getDateOnly(
        defaultChatPayload?.prescription_measurement_date,
      ),
    };
  }, [defaultChatPayload, userFullName, userId]);

  const getChatObject = useCallback(
    ({ createdAt, id, message, modifiedAt, senderEnum }: TGetChatObject) => ({
      ...defaultChatProps,
      id,
      sender_enum: senderEnum,
      message,
      created_at: createdAt,
      modified_at: modifiedAt,
    }),
    [defaultChatProps],
  );

  const handleScrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
    setShowScrollToBottom(false);
  }, []);

  const onScrollToBottom = useCallback(() => {
    handleScrollToBottom();
  }, [handleScrollToBottom]);

  const followLatestMessageScrollCustomRef = useRef<
    ((options?: { immediate?: boolean }) => void) | null
  >(null);

  const setLatestMessageFollowScroll = useCallback(
    (fn: ((options?: { immediate?: boolean }) => void) | null) => {
      followLatestMessageScrollCustomRef.current = fn;
    },
    [],
  );

  const followLatestMessageAfterSend = useCallback(
    (followScrollOptions?: { immediate?: boolean }) => {
      const custom = followLatestMessageScrollCustomRef.current;
      if (custom) {
        custom(followScrollOptions);
        return;
      }
      onScrollToBottom();
    },
    [onScrollToBottom],
  );

  const handleSetUserChat = useCallback(
    (message: string) => {
      setCurrentChat((prev) => [
        ...prev,
        getChatObject({
          message,
          id: uuidv4(),
          senderEnum: "user",
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
        }),
      ]);
    },
    [getChatObject],
  );

  const handleSetBotChat = useCallback(
    (messageChunk: string, chatObject: TKemMessagePayload) => {
      setCurrentChat((prev) => {
        const newChats = [...prev];
        const chatIndex = newChats.findIndex((c) => c.id === chatObject.id);

        if (chatIndex !== -1) {
          newChats[chatIndex] = {
            ...newChats[chatIndex],
            message: `${newChats[chatIndex].message}${messageChunk}`,
          };
        } else {
          newChats.push({ ...chatObject, message: messageChunk });
        }

        return newChats;
      });
    },
    [],
  );

  const handleSetBotMessageId = useCallback((oldId: string, newId: string) => {
    setCurrentChat((prev) => {
      const newChats = [...prev];
      const chatIndex = newChats.findIndex((c) => c.id === oldId);

      if (chatIndex !== -1) {
        newChats[chatIndex] = { ...newChats[chatIndex], id: newId };
      }

      return newChats;
    });
  }, []);

  const handleSetChatAudioUrl = useCallback((chatId: string, url: string) => {
    setCurrentChat((prev) => {
      const chatIndex = prev.findIndex((c) => c.id === chatId);

      if (chatIndex !== -1) {
        return [
          ...prev.slice(0, chatIndex),
          { ...prev[chatIndex], output_audio_url: url },
          ...prev.slice(chatIndex + 1),
        ];
      }

      return prev;
    });
  }, []);

  const loadChatHistory = useCallback(async () => {
    if (!userId) {
      return;
    }

    const keySignature = sessionKey ?? "";

    try {
      hasLoadedHistoryRef.current = false;
      setIsLoadingHistory(true);

      if (!sessionKey) {
        setCurrentChat([]);
        shouldSendMemberPayloadRef.current = true;
        hasLoadedHistoryRef.current = true;
        lastHistoryLoadKeyRef.current = keySignature;
        return;
      }

      try {
        const history = await getChatHistory();
        setCurrentChat(history);
        shouldSendMemberPayloadRef.current = history.length === 0;
      } catch (err) {
        console.error("[useKemCoachChat] Failed to load Kem chat history", err);
        setCurrentChat([]);
        shouldSendMemberPayloadRef.current = true;
      }
      hasLoadedHistoryRef.current = true;
      lastHistoryLoadKeyRef.current = keySignature;
    } finally {
      setIsLoadingHistory(false);
    }
  }, [getChatHistory, userId, sessionKey]);

  const handleGetNextRecentHistory = useCallback(async (): Promise<number> => {
    if (getIsLastPage()) return 0;

    try {
      setIsLoadingMoreHistory(true);
      const nextHistory = await getNextChatHistory();

      if (nextHistory.length === 0) return 0;

      setCurrentChat((prev) => {
        const allChats = [...nextHistory, ...prev];
        return uniqBy(allChats, (chat) => chat.id);
      });

      return nextHistory.length;
    } finally {
      setIsLoadingMoreHistory(false);
    }
  }, [getIsLastPage, getNextChatHistory]);

  const buildMemberPayload = useCallback(
    (): KemCoachMemberPayload => ({
      gender: defaultChatPayload?.gender,
      date_of_birth: defaultChatPayload?.date_of_birth,
      latest_bmi: defaultChatPayload?.latest_bmi
        ? Number(defaultChatPayload.latest_bmi.split(" ")[0])
        : undefined,
      bmi_measurement_date: defaultChatPayload?.bmi_measurement_date,
      latest_bp: defaultChatPayload?.latest_bp,
      bp_measurement_date: defaultChatPayload?.bp_measurement_date,
      pregnancy_status: defaultChatPayload?.pregnancy_status,
      pregnancy_edd: defaultChatPayload?.pregnancy_edd,
      exercise_duration_last_30_days:
        defaultChatPayload?.exercise_duration_last_30_days?.toString(),
      latest_blood_sugar: defaultChatPayload?.latest_blood_sugar,
      blood_sugar_measurement_date:
        defaultChatPayload?.blood_sugar_measurement_date,
      latest_blood_sugar_meal_type:
        defaultChatPayload?.latest_blood_sugar_meal_type,
      latest_cholesterol: defaultChatPayload?.latest_cholesterol,
      cholesterol_measurement_date:
        defaultChatPayload?.cholesterol_measurement_date,
      latest_weight: defaultChatPayload?.latest_weight,
      weight_measurement_date: defaultChatPayload?.weight_measurement_date,
      latest_weight_goal: defaultChatPayload?.latest_weight_goal,
      latest_height: defaultChatPayload?.latest_height,
      height_measurement_date: defaultChatPayload?.height_measurement_date,
      latest_waist_circumference:
        defaultChatPayload?.latest_waist_circumference,
      waist_circumference_measurement_date:
        defaultChatPayload?.waist_circumference_measurement_date,
      waist_circumference_goal: defaultChatPayload?.waist_circumference_goal,
      latest_hbaic: defaultChatPayload?.latest_hbaic,
      hbaic_measurement_date: defaultChatPayload?.hbaic_measurement_date,
      latest_total_psa: defaultChatPayload?.latest_total_psa,
      latest_free_psa: defaultChatPayload?.latest_free_psa,
      latest_psa_file_name: defaultChatPayload?.latest_psa_file_name,
      psa_measurement_date: defaultChatPayload?.psa_measurement_date,
      latest_eye_examination: defaultChatPayload?.latest_eye_examination,
      eye_examination_measurement_date:
        defaultChatPayload?.eye_examination_measurement_date,
      latest_foot_examination: defaultChatPayload?.latest_foot_examination,
      foot_examination_measurement_date:
        defaultChatPayload?.foot_examination_measurement_date,
      latest_health_condition: defaultChatPayload?.latest_health_condition,
      health_condition_measurement_date:
        defaultChatPayload?.health_condition_measurement_date,
      latest_procedure_surgeries:
        defaultChatPayload?.latest_procedure_surgeries,
      procedure_surgeries_measurement_date:
        defaultChatPayload?.procedure_surgeries_measurement_date,
      lastest_prescription: defaultChatPayload?.lastest_prescription,
      prescription_measurement_date:
        defaultChatPayload?.prescription_measurement_date,
      current_week: currentWeek,
      user_id: userId,
      session_key: sessionKey,
      coach_details: defaultChatPayload?.coach_details,
      macro_goals: defaultChatPayload?.macro_goals,
    }),
    [defaultChatPayload, currentWeek, userId, sessionKey],
  );

  const initializeSocket = useCallback(() => {
    if (socketRef.current) {
      console.log("[useKemCoachChat] Socket already initialized, skipping");
      return;
    }

    console.log("[useKemCoachChat] Initializing socket...");
    const socket = new KemCoachSocket();
    socketRef.current = socket;

    void socket.connect({
      onStateChange: (state) => {
        console.log("[useKemCoachChat] Socket state changed:", state);
        setSocketState(state);

        if (state === "STREAMING") {
          setLoadingBotChat("streaming");
        } else if (state === "COMPLETE" || state === "READY") {
          setLoadingBotChat(undefined);
        }
      },

      onChunk: (chunk) => {
        currentBotMessageIdRef.current ??= uuidv4();

        handleSetBotChat(
          chunk,
          getChatObject({
            createdAt: new Date().toISOString(),
            id: currentBotMessageIdRef.current,
            modifiedAt: new Date().toISOString(),
            senderEnum: "kem",
            message: "",
          }),
        );
      },

      onComplete: (data: KemCoachCompletionData) => {
        const tempId = currentBotMessageIdRef.current;

        if (data.session_key && data.session_key.trim() !== "") {
          if (currentWeek === "-1") {
            dispatch(
              updateAppKemState({
                activeICSessionKey: data.session_key,
              } as Partial<IKemState>),
            );
          } else {
            dispatch(
              updateAppKemState({
                activeSessionKey: data.session_key,
              } as Partial<IKemState>),
            );
          }
        }

        if (
          tempId &&
          data.kem_message_id &&
          data.kem_message_id.trim() !== ""
        ) {
          handleSetBotMessageId(tempId, data.kem_message_id);
        }

        if (
          data.output_audio_url &&
          data.output_audio_url.trim() !== "" &&
          tempId
        ) {
          handleSetChatAudioUrl(tempId, data.output_audio_url);
        }

        if (data.infographic && data.infographic.trim() !== "") {
          currentBotMessageIdRef.current ??= uuidv4();

          handleSetBotChat(
            `\n\n${data.infographic}`,
            getChatObject({
              createdAt: new Date().toISOString(),
              id: currentBotMessageIdRef.current,
              modifiedAt: new Date().toISOString(),
              senderEnum: "kem",
              message: "",
            }),
          );
        }

        setCurrentQuickOptions(data.options ?? []);

        currentBotMessageIdRef.current = null;

        if (currentWeek === "-1") {
          LogEvent(FirebaseEventEnum.KEM_IC_CHAT_MESSAGE, {
            timestamp: new Date().toISOString(),
            type: "incoming",
          });
        } else {
          LogEvent(FirebaseEventEnum.KEM_COACH_CHAT_MESSAGE, {
            timestamp: new Date().toISOString(),
            type: "incoming",
          });
        }

        if (data.completion_progress !== undefined) {
          applyCompletionProgress(data.completion_progress);
        }

        if (data.session_completed) {
          applyCompletionProgress(100);
          if (typeof window !== "undefined" && currentWeek === "-1") {
            sessionStorage.setItem(
              KEM_IC_COMPLETION_PENDING_STORAGE_KEY,
              "true",
            );
          }
          void initiateKemUserSession().finally(() => {
            onOpenSessionCompletedModal();
          });
        }

        followLatestMessageAfterSend({ immediate: true });
      },

      onError: (error: unknown) => {
        if (!isMountedRef.current) return;

        const msg = toErrorMessage(error);

        if (msg === "unauthorized") {
          console.error("[useKemCoachChat] Session expired");
          void logoutUser();
        }
      },

      onMemberAck: () => {},
    });
  }, [
    LogEvent,
    currentWeek,
    dispatch,
    getChatObject,
    logoutUser,
    handleSetBotChat,
    handleSetBotMessageId,
    handleSetChatAudioUrl,
    initiateKemUserSession,
    onOpenSessionCompletedModal,
    applyCompletionProgress,
    followLatestMessageAfterSend,
  ]);

  const handleSendMessage = useCallback(
    async (props: TSendMessageProps) => {
      if (!navigator.onLine) {
        console.error("[useKemCoachChat] No internet connection");
        return;
      }

      if (!userId) {
        console.error("[useKemCoachChat] User not found");
        return;
      }

      if (!props.message) {
        console.warn("[useKemCoachChat] Message cannot be empty");
        return;
      }

      if (socketState === "ERROR" && socketRef.current) {
        console.info("[useKemCoachChat] Reconnecting...");
        socketRef.current.reconnect();
        return;
      }

      if (socketState === "RECONNECTING") {
        console.info("[useKemCoachChat] Reconnecting, please wait...");
        return;
      }

      if (!socketRef.current?.isConnected()) {
        console.error("[useKemCoachChat] Socket is not connected");
        return;
      }

      if (socketState === "STREAMING") {
        console.info(
          "[useKemCoachChat] Please wait for the current response to complete",
        );
        return;
      }

      handleSetUserChat(props.message.trim());
      followLatestMessageAfterSend();
      setCurrentQuickOptions([]);

      if (currentWeek === "-1") {
        LogEvent(FirebaseEventEnum.KEM_IC_CHAT_MESSAGE, {
          timestamp: new Date().toISOString(),
          type: "outgoing",
          messageCharCount: props.message.trim().length,
        });
      } else {
        LogEvent(FirebaseEventEnum.KEM_COACH_CHAT_MESSAGE, {
          timestamp: new Date().toISOString(),
          type: "outgoing",
          messageCharCount: props.message.trim().length,
        });
      }

      setLoadingBotChat("sending");
      socketRef.current.sendChatMessage(props.message.trim());
    },
    [
      userId,
      socketState,
      followLatestMessageAfterSend,
      handleSetUserChat,
      setCurrentQuickOptions,
      currentWeek,
      LogEvent,
    ],
  );

  const handleCancelCurrentBotMessage = useCallback(() => {
    console.info(
      "[useKemCoachChat] Please wait for the current response to complete",
    );
  }, []);

  const handleRetryConnection = useCallback(() => {
    if (socketRef.current) {
      console.log("[useKemCoachChat] Manual retry connection requested");
      socketRef.current.reconnect();
    }
  }, []);

  useEffect(() => {
    if (!userId || !isKemIC || kemUserSession) return;
    void initiateKemUserSession();
  }, [userId, isKemIC, kemUserSession, initiateKemUserSession]);

  useEffect(() => {
    if (typeof window === "undefined" || !isKemIC) return;

    if (sessionCompleted) {
      const isCompletionPending =
        sessionStorage.getItem(KEM_IC_COMPLETION_PENDING_STORAGE_KEY) ===
        "true";
      if (isCompletionPending) {
        onOpenSessionCompletedModal();
      }
    } else {
      sessionStorage.removeItem(KEM_IC_COMPLETION_PENDING_STORAGE_KEY);
    }
  }, [isKemIC, sessionCompleted, onOpenSessionCompletedModal]);

  useEffect(() => {
    if (!isKemIC) return;

    const hasJustCompleted =
      prevSessionCompletedRef.current === false && sessionCompleted;

    if (hasJustCompleted) {
      if (typeof window !== "undefined") {
        sessionStorage.setItem(KEM_IC_COMPLETION_PENDING_STORAGE_KEY, "true");
      }
      onOpenSessionCompletedModal();
    }

    prevSessionCompletedRef.current = sessionCompleted;
  }, [isKemIC, sessionCompleted, onOpenSessionCompletedModal]);

  useEffect(() => {
    if (!userId) return;
    const keySignature = sessionKey ?? "";
    if (
      lastHistoryLoadKeyRef.current !== null &&
      lastHistoryLoadKeyRef.current === keySignature
    ) {
      return;
    }

    // Do not refetch/replace history once messages are in state
    // (e.g. after activeICSessionKey updates post member-payload stream).
    // This fixes the phantom "hi" that come sup from nowhere
    if (currentChat.length > 0) {
      lastHistoryLoadKeyRef.current = keySignature;
      return;
    }

    void loadChatHistory();
  }, [loadChatHistory, sessionKey, userId, currentChat.length]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (socketState === "DISCONNECTED" && !socketRef.current) {
      if (defaultChatPayload) {
        initializeSocket();
      } else {
        timeoutId = setTimeout(() => {
          if (socketState === "DISCONNECTED" && !socketRef.current) {
            console.warn(
              "[useKemCoachChat] Initializing socket without defaultChatPayload",
            );
            initializeSocket();
          }
        }, 500);
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [defaultChatPayload, socketState, initializeSocket]);

  useEffect(() => {
    if (
      hasLoadedHistoryRef.current &&
      socketState === "CONNECTED" &&
      socketRef.current?.isConnected() &&
      defaultChatPayload
    ) {
      const memberPayload = buildMemberPayload();
      socketRef.current.sendMemberPayload(memberPayload);

      if (shouldSendMemberPayloadRef.current && currentChat.length === 0) {
        setLoadingBotChat("sending");
      }

      shouldSendMemberPayloadRef.current = false;
    }
  }, [socketState, defaultChatPayload, buildMemberPayload, currentChat.length]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        socketRef.current &&
        !socketRef.current.isConnected() &&
        (socketState === "ERROR" || socketState === "DISCONNECTED")
      ) {
        console.log(
          "[useKemCoachChat] Tab became visible and socket disconnected, reconnecting",
        );

        setTimeout(() => {
          if (socketRef.current && !socketRef.current.isConnected()) {
            socketRef.current.reconnect();
          }
        }, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [socketState]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollToBottom(distanceFromBottom > el.clientHeight * 2);
  }, []);

  const keyExtractor = useCallback(
    (item: TKemMessagePayload) => `${item.id}_${item.created_at}`,
    [],
  );

  return {
    handleSendMessage,
    isLoadingHistory,
    isInitialHistoryLoading,
    loadingBotChat,
    scrollContainerRef,
    handleScroll,
    userId,
    handleCancelCurrentBotMessage,
    handleGetNextRecentHistory,
    getIsLastPage,
    currentChat,
    isLoadingMoreHistory,
    kemIsTyping,
    kemSocketReachable,
    isConnecting,
    showScrollToBottom,
    setLatestMessageFollowScroll,
    onScrollToBottom,
    socketState,
    isReady,
    keyExtractor,
    sessionCompleted,
    isSessionCompletedModalOpen,
    onOpenSessionCompletedModal,
    onCloseSessionCompletedModal,
    handleRetryConnection,
    currentQuickOptions,
    setCurrentQuickOptions,
  };
};

export default useKemCoachChat;
